// Agreements Slice 3 (#901). The client-side half of the checkout_url
// allow-list (design F-D6). 3a (backend/agreements-service) validates the
// same value against config('services.elemis.checkout_hosts') before it is
// persisted or returned; this is the SECOND check, applied a second time on
// our side of the wire, immediately before the value is assigned to an
// iframe `src`.
//
// This is not redundant. The value crosses a trust boundary (the network)
// between the two checks, and the consequence of getting it wrong is an
// arbitrary origin framed inside our own authenticated page. A
// misconfigured or empty backend allow-list must not be able to turn our
// summary screen into a delivery vehicle for someone else's content.

/** The three elemis checkout hosts, from
 *  docs/integrations/elemis-payment-schedule-contract.md §2. Mirrors the
 *  backend's config('services.elemis.checkout_hosts') default. */
export const DEFAULT_ELEMIS_CHECKOUT_HOSTS = [
    'app.sandbox.motorplanpayments.co.uk',
    'app.staging.motorplanpayments.co.uk',
    'app.motorplanpayments.co.uk',
] as const;

const MAX_CHECKOUT_URL_LENGTH = 2048;

/** UNSET falls back to the three defaults. SET-BUT-EMPTY (e.g. "," or "   ")
 *  yields an empty list, which denies everything — fail closed, mirroring
 *  elemis's own IP-allow-list semantics and the backend's. The two cases are
 *  deliberately different: "not configured" is not the same statement as
 *  "configured to nothing". */
export function allowedCheckoutHosts(): string[] {
    const raw = import.meta.env.VITE_ELEMIS_CHECKOUT_HOSTS;
    if (typeof raw !== 'string' || raw.trim() === '') return [...DEFAULT_ELEMIS_CHECKOUT_HOSTS];
    return raw
        .split(',')
        .map((h) => h.trim().toLowerCase())
        .filter((h) => h !== '');
}

/** SECOND line of defence (F-D6). The backend validated this before
 *  persisting it, but the value crosses the network between the two checks
 *  and the consequence of being wrong is an arbitrary origin framed inside
 *  our authenticated page. `new URL()` also closes the userinfo trick: the
 *  hostname of `https://app.motorplanpayments.co.uk@evil.example/` is
 *  `evil.example`, not `app.motorplanpayments.co.uk`.
 *
 *  Fail closed: an unparseable URL, a non-`https:` scheme, a host outside
 *  the list, an over-long value, or an explicitly-configured-but-empty list
 *  all return `false` — no iframe is ever rendered for them. */
export function isEmbeddableCheckoutUrl(value: unknown): value is string {
    if (typeof value !== 'string' || value === '' || value.length > MAX_CHECKOUT_URL_LENGTH) {
        return false;
    }
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return false;
    }
    if (url.protocol !== 'https:') return false;
    const hosts = allowedCheckoutHosts();
    if (hosts.length === 0) return false; // fail closed
    return hosts.includes(url.hostname.toLowerCase());
}
